import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Eye, RefreshCw, Loader2, Save, Info } from 'lucide-react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SCOPE_META = {
  all: { label: 'Tout voir', color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  team: { label: 'Équipe', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  assigned_ops: { label: 'Assigné (ops)', color: 'bg-violet-100 text-violet-800 border-violet-200' },
  assigned_field: { label: 'Assigné (terrain)', color: 'bg-violet-100 text-violet-800 border-violet-200' },
  assigned_commercial: { label: 'Assigné (commercial)', color: 'bg-violet-100 text-violet-800 border-violet-200' },
  company: { label: 'Société', color: 'bg-amber-100 text-amber-800 border-amber-200' },
  own: { label: 'Personnel', color: 'bg-orange-100 text-orange-800 border-orange-200' },
};

const SCOPE_HELP = {
  all: 'Voit toutes les données du domaine (rôles de supervision).',
  team: "Voit les enregistrements assignés à lui-même + ses subordonnés (manager_id / team_id).",
  assigned_ops: 'Voit uniquement ce qui lui est assigné (assignedOperatorId).',
  assigned_field: 'Voit uniquement ce qui lui est assigné (assignedFieldOpsId).',
  assigned_commercial: 'Voit uniquement ses clients/opérations assignés (assignedCommercialId).',
  company: 'Voit les données de sa société (clients / expéditeurs).',
  own: 'Voit uniquement ses propres enregistrements.',
};

const DataScopeManager = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState({});

  const headers = { Authorization: 'Bearer cookie-auth' };

  const fetchScopes = async (isRefresh = false) => {
    isRefresh ? setRefreshing(true) : setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/rbac-audit/data-scopes`, { headers });
      setData(res.data);
      const d = {};
      (res.data.effective || []).forEach((r) => { d[r.role] = r.scope; });
      setDrafts(d);
    } catch (e) {
      toast({
        title: 'Erreur',
        description: e?.response?.status === 403
          ? "Accès refusé : permission 'roles.view' requise."
          : "Impossible de charger la config de data-scoping.",
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchScopes(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveScope = async (role) => {
    setSaving((p) => ({ ...p, [role]: true }));
    try {
      await axios.put(`${API}/admin/rbac-audit/data-scopes/${role}`, { scope: drafts[role] }, { headers });
      toast({ title: 'Scope mis à jour', description: `${role} → ${SCOPE_META[drafts[role]]?.label || drafts[role]}` });
      await fetchScopes(true);
    } catch (e) {
      toast({
        title: 'Erreur',
        description: e?.response?.status === 403
          ? "Accès refusé : permission 'roles.edit' requise."
          : (e?.response?.data?.detail || 'Échec de la mise à jour.'),
        variant: 'destructive',
      });
    } finally {
      setSaving((p) => ({ ...p, [role]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48" data-testid="data-scope-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#1db584]" />
      </div>
    );
  }
  if (!data) return null;

  const effective = data.effective || [];
  const strategies = data.strategies || Object.keys(SCOPE_META);

  return (
    <div className="space-y-6" data-testid="data-scope-manager">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-2">
            <Eye className="w-5 h-5 text-blue-600" /> Data-scoping par rôle
          </h2>
          <p className="text-sm text-gray-500">
            Définit la <strong>visibilité des données</strong> (quelles lignes un rôle voit) — distinct des permissions d'accès.
            Source : <code className="text-xs">rbac_data_scopes</code> · cache : <strong>{data.cache?.source}</strong>.
          </p>
        </div>
        <Button onClick={() => fetchScopes(true)} disabled={refreshing} variant="outline" className="gap-2" data-testid="data-scope-refresh-btn">
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Rafraîchir
        </Button>
      </div>

      <Card className="bg-blue-50/50 border-blue-200">
        <CardContent className="pt-4 flex gap-2 text-sm text-blue-800">
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            Le scope <strong>team</strong> nécessite que les utilisateurs aient un <code className="text-xs">manager_id</code> /
            <code className="text-xs"> team_id</code>. Sans membres résolus, il se comporte comme « Tout voir » (anti-lockout).
          </span>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Stratégie de visibilité ({effective.length} rôles)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {effective.map((r) => {
            const meta = SCOPE_META[r.scope] || { label: r.scope, color: 'bg-gray-100 text-gray-700' };
            const changed = drafts[r.role] !== r.scope;
            return (
              <div key={r.role} className="flex items-center gap-3 border rounded-lg px-3 py-2.5" data-testid={`data-scope-row-${r.role}`}>
                <span className="font-mono text-sm text-gray-800 w-40 truncate">{r.role}</span>
                <Badge className={`${meta.color} hidden sm:inline-flex`}>{meta.label}</Badge>
                <div className="flex-1" />
                <Select value={drafts[r.role]} onValueChange={(v) => setDrafts((p) => ({ ...p, [r.role]: v }))}>
                  <SelectTrigger className="w-56" data-testid={`data-scope-select-${r.role}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {strategies.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`data-scope-option-${r.role}-${s}`}>
                        {SCOPE_META[s]?.label || s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  disabled={!changed || saving[r.role]}
                  onClick={() => saveScope(r.role)}
                  className="gap-1.5"
                  data-testid={`data-scope-save-${r.role}`}
                >
                  {saving[r.role] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  Enregistrer
                </Button>
              </div>
            );
          })}
          {drafts && Object.keys(drafts).length > 0 && (
            <p className="text-xs text-gray-400 pt-2">
              {SCOPE_HELP[drafts[effective[0]?.role]] || 'Choisissez une stratégie par rôle puis enregistrez.'}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DataScopeManager;
